import { useTheme } from 'lib/context/ThemeContext';
import React, { useEffect, useRef, useState } from 'react';
import { VideoListingTheme } from './VideoGallery.theme';
import { Pager as HeadlessPager } from '@coveo/headless';
import Headline from '../Headline/Headline';
import { VideoGalleryProps } from 'components/general/VideoGallery/VideoGallery';
import { getBreakpoint, useCurrentScreenType } from 'lib/utils/get-screen-type';
import { getEnum } from 'lib/utils/get-enum';
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';
import { ImagePrimaryProps } from '../Media/ImagePrimary';
import { ImagePrimaryProps as VideoPrimaryProps } from '../Media/MediaPrimary';
import Pagination from './Pagination.Helper';
import VideoPlayer from './VideoPlayer.Helper';
import VideoDetails from './VideoDetails.Helper';
import { SliderWrapper } from '../SliderWrapper';
import VideoGalleryItem from './VideoGalleryItem.Helper';
import { useModalIdContext } from 'lib/context/GenericModalIDContext';
import { getPagerTheme } from 'components/search/Search/Search.Pager.theme';
import { Pager } from 'src/helpers/Coveo/Pager/Pager';
import { SliderRefType, SliderType } from '../SliderWrapper/SliderWrapper';
import { useCurrentVideoStore } from './VideoListing.store';
import { useRouter } from 'next/router';

type MobileGalleryStyles = 'list' | 'sidescroll';
export type DesktopGalleryStyles =
  | MobileGalleryStyles
  | 'playlist'
  | 'sidescrollwithoutvideo'
  | 'sidescrollwithoutvideoandfield';
export type VideoItemProps = (ImagePrimaryProps | VideoPrimaryProps) & {
  fields: { createdBy: Field<string>; postedDate: Field<string> };
};

const MAX_IFRAME_LOOKUP_COUNT = 5;

const VideoListing = (
  props: VideoGalleryProps & {
    isCoveoDrivenPagination?: boolean;
    pagerController?: HeadlessPager;
    isInContainerRail?: boolean;
    resolveDeepLinkedVideo?: (requestedVideoValue: string) => Promise<unknown>;
  }
) => {
  const { themeData, themeName } = useTheme(VideoListingTheme);
  const pagerThemeData = getPagerTheme(themeName);

  const {
    fields,
    isCoveoDrivenPagination = false,
    pagerController,
    isInContainerRail = false,
  } = props;

  const { currentScreenWidth } = useCurrentScreenType();
  const { setSelectedModalId } = useModalIdContext();

  const {
    currentPlayingVideo,
    setCurrentPlayingVideo,
    selectedVideoIndex,
    setSelectedVideoIndex,
    tabClicked,
    setTabClicked,
  } = useCurrentVideoStore();

  //#region States
  const [isVideoModalVisible, setIsVideoModalVisible] = useState(false);
  const [coveoPagerState, setCoveoPagerState] = useState<HeadlessPager['state']>(
    pagerController?.state
  );
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [activeVideoAnchorId, setActiveVideoAnchorId] = useState<string | undefined>();
  //#endregion

  const hasUserSelectedVideoRef = useRef(false);
  const hasHandledDeepLinkRef = useRef(false);
  const sliderRef = useRef<SliderType>();
  const playerRegion = useRef<HTMLDivElement>(null);

  const VIDEO_QUERY_PARAM = 'video';
  const normalize = (v?: string | null) => (v || '').trim().toLowerCase();

  const getPrimaryVideoFields = (item: unknown) => {
    const maybeFields = (item as { fields?: unknown })?.fields;

    if (!maybeFields || typeof maybeFields !== 'object') {
      return undefined;
    }

    const primaryVideo = (maybeFields as { primaryVideo?: unknown })?.primaryVideo;

    if (!primaryVideo || typeof primaryVideo !== 'object') {
      return undefined;
    }

    const primaryVideoFields = (primaryVideo as { fields?: unknown })?.fields;

    if (!primaryVideoFields || typeof primaryVideoFields !== 'object') {
      return undefined;
    }

    return primaryVideoFields as {
      videoId?: Field<string>;
      videoAnchorId?: Field<string>;
    };
  };

  const getVideoIdFromItem = (item: unknown) => {
    return getPrimaryVideoFields(item)?.videoId?.value;
  };

  const getLookupValueFromItem = (item: unknown) => {
    const primaryFields = getPrimaryVideoFields(item);
    return primaryFields?.videoAnchorId?.value;
  };

  const router = useRouter();
  const safeAsPath = router?.asPath || '';

  const getRequestedVideoAnchorIdFromUrl = () => {
    if (typeof window === 'undefined') {
      return null;
    }

    const asPath = safeAsPath || window.location.href;
    const queryString = asPath.includes('?') ? asPath.split('?')[1] : '';
    const cleanQueryString = queryString.split('#')[0];

    return new URLSearchParams(cleanQueryString).get(VIDEO_QUERY_PARAM);
  };

  const findVideoIndexInCurrentPageByAnchorId = (anchorIdFromUrl: string) => {
    const wanted = normalize(anchorIdFromUrl);
    const videos = (fields?.videos || []) as VideoItemProps[];

    return videos.findIndex((v: VideoItemProps) => {
      const anchorId = getLookupValueFromItem(v);
      return normalize(anchorId) === wanted;
    });
  };

  useEffect(() => {
    setSelectedVideoIndex(window.innerWidth < 1008 ? -1 : 0);
  }, [setSelectedVideoIndex]);

  useEffect(() => {
    const handleSliderNext = (e: Event) => {
      const target = e.target as HTMLElement;

      if (
        target.closest('.slick-slider > .slick-next') ||
        target.closest('.slick-slider > .slick-prev')
      ) {
        setTabClicked(true);
      }
    };

    window.addEventListener('touchstart', handleSliderNext);

    return () => {
      window.removeEventListener('touchstart', handleSliderNext);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const run = async () => {
      if (
        typeof window === 'undefined' ||
        hasUserSelectedVideoRef.current ||
        hasHandledDeepLinkRef.current
      ) {
        return;
      }

      const requestedVideoValue = getRequestedVideoAnchorIdFromUrl();
      if (!requestedVideoValue) {
        return;
      }

      const pageIndex = findVideoIndexInCurrentPageByAnchorId(requestedVideoValue);
      if (pageIndex >= 0) {
        const item = fields?.videos?.[pageIndex];
        const matchedLookupValue = getLookupValueFromItem(item);
        const videoId = getVideoIdFromItem(item);

        if (normalize(matchedLookupValue) === normalize(requestedVideoValue) && videoId) {
          setSelectedVideoIndex(pageIndex);
          setCurrentPlayingVideo(item as unknown as VideoItemProps);
          setActiveVideoAnchorId(getLookupValueFromItem(item));
          selectVideo(videoId, pageIndex, item as unknown as VideoItemProps, true);
          return;
        }
      }

      if (props.resolveDeepLinkedVideo) {
        const foundVideo = await props.resolveDeepLinkedVideo(requestedVideoValue);

        if (!foundVideo) {
          return;
        }

        const matchedLookupValue = getLookupValueFromItem(foundVideo);
        const videoId = getVideoIdFromItem(foundVideo);

        if (normalize(matchedLookupValue) !== normalize(requestedVideoValue) || !videoId) {
          return;
        }

        setSelectedVideoIndex(-1); // no visible card selected
        setCurrentPlayingVideo(foundVideo as VideoItemProps);
        setActiveVideoAnchorId(getLookupValueFromItem(foundVideo));
        selectVideo(videoId, -1, foundVideo as VideoItemProps, true);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeAsPath, fields?.videos, props.resolveDeepLinkedVideo]);

  useEffect(() => {
    if (!pagerController) {
      return;
    }
    const unsubscribe = pagerController.subscribe(() => {
      setCoveoPagerState(pagerController.state);
    });

    return unsubscribe;
  }, [pagerController]);

  useEffect(() => {
    if (tabClicked) {
      setSelectedVideoIndex(-1);
      return;
    }

    if (currentPlayingVideo) {
      return;
    }

    if (selectedVideoIndex < 0) {
      return;
    }

    const videos = fields?.videos;

    const newVideo =
      !videos?.length || selectedVideoIndex >= videos.length
        ? undefined
        : (videos[selectedVideoIndex] as unknown as VideoItemProps);

    if (!newVideo) {
      return;
    }

    setCurrentPlayingVideo(newVideo);
    setActiveVideoAnchorId(getLookupValueFromItem(newVideo));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVideoIndex, fields?.videos, currentPlayingVideo]);

  // Handle page change events for coveo pager
  useEffect(() => {
    if (isCoveoDrivenPagination && coveoPagerState) {
      setCurrentPageIndex(coveoPagerState.currentPage - 1);
    }

    // 'coveoPagerState' and 'isCoveoDrivenPagination' are configured for coveo driven state and pagination;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coveoPagerState?.currentPage]);

  if (!fields || !fields?.videos) {
    return null;
  }

  const videosPerPage = isCoveoDrivenPagination ? fields?.videos.length || 5 : 5;
  const displayStyle =
    getEnum<DesktopGalleryStyles>(
      currentScreenWidth < getBreakpoint('ml')
        ? fields?.mobileDisplayStyle
        : fields?.desktopDisplayStyle
    ) || 'list';

  const isMinimalSideScrollVariant =
    displayStyle === 'sidescrollwithoutvideo' || displayStyle === 'sidescrollwithoutvideoandfield';

  const layoutClasses = themeData.classes?.layouts?.[displayStyle] || 'list';

  const isDesktop = currentScreenWidth >= getBreakpoint('md');

  const sliderSettings = {
    // React-slick's responsive functionality does not work properly here that's why we've opt for the conditional slider settings
    infinite: false,
    speed: 500,
    dots:
      currentScreenWidth < getBreakpoint('ml') || displayStyle === 'sidescrollwithoutvideoandfield',
    slidesToShow:
      isInContainerRail && isDesktop
        ? 2
        : currentScreenWidth < getBreakpoint('md')
        ? 1
        : currentScreenWidth < getBreakpoint('ml')
        ? 2
        : 4,
    slidesToScroll: 1,
    initialSlide:
      currentScreenWidth < getBreakpoint('ml') && displayStyle === 'sidescroll'
        ? 0
        : selectedVideoIndex,
    enableNumberedPagination:
      currentScreenWidth < getBreakpoint('ml') || displayStyle === 'sidescrollwithoutvideoandfield',
    afterIndexChange: () => currentScreenWidth < getBreakpoint('ml') && setSelectedVideoIndex(-1),
  };

  //#region Logic functions
  const playStopVideo = (
    videoId: string,
    action: 'playVideo' | 'stopVideo',
    iframeLookupTryCount = 3
  ) => {
    try {
      const videoIframe = document.getElementById(videoId) as HTMLIFrameElement;

      if (!videoIframe && iframeLookupTryCount <= MAX_IFRAME_LOOKUP_COUNT) {
        setTimeout(() => playStopVideo(videoId, action, ++iframeLookupTryCount), 1000);
      } else {
        videoIframe.contentWindow?.postMessage(
          `{"event":"command", "func":"${action}", "args":""}`,
          '*'
        );
      }
    } catch (_) {
      console.error("ERROR! Can't find Video iFrame");
    }
  };

  const focusPlayerRegion = (opts?: { smooth?: boolean; center?: boolean }) => {
    const el = playerRegion.current;

    if (!el) {
      return;
    }

    el.focus({ preventScroll: true });
    el.scrollIntoView({
      behavior: opts?.smooth ? 'smooth' : 'auto',
      block: opts?.center ? 'center' : 'nearest',
    });
  };

  function selectVideo(
    videoId: string,
    videoItemIndex: number,
    selectedVideo?: VideoItemProps,
    isDeepLinkSelection = false
  ) {
    setSelectedVideoIndex(videoItemIndex);

    if (!isDeepLinkSelection) {
      hasUserSelectedVideoRef.current = true;
    }

    if (selectedVideo) {
      setCurrentPlayingVideo(selectedVideo);
      setActiveVideoAnchorId(getLookupValueFromItem(selectedVideo));
    }

    if (currentScreenWidth < getBreakpoint('ml') || isMinimalSideScrollVariant) {
      setIsVideoModalVisible(true);
      setSelectedModalId('VideoItemModal');
    }

    requestAnimationFrame(() => {
      setTimeout(() => {
        focusPlayerRegion({ smooth: true, center: true });
      }, 60);
    });

    playStopVideo(videoId, 'playVideo');
  }

  const closeModal = () => {
    hasHandledDeepLinkRef.current = true;
    try {
      const videoId = (
        (currentPlayingVideo as unknown as VideoPrimaryProps).fields?.primaryVideo?.fields
          ?.videoId as Field<string>
      )?.value;

      playStopVideo(videoId, 'stopVideo');
      setSelectedModalId('');
      setIsVideoModalVisible(false);
    } catch (error) {
      setSelectedModalId('');
      setIsVideoModalVisible(false);
    }
  };

  //#endregion

  const isActiveVideoCard = (video: VideoItemProps) => {
    const videoAnchorId = getLookupValueFromItem(video);

    return (
      !!videoAnchorId &&
      !!activeVideoAnchorId &&
      normalize(videoAnchorId) === normalize(activeVideoAnchorId)
    );
  };

  //#region Rendering functions
  const renderVideoCardsList = () => {
    let _videos = fields?.videos;

    if (!isCoveoDrivenPagination && displayStyle === 'list') {
      _videos = _videos.slice(
        currentPageIndex * videosPerPage,
        (currentPageIndex + 1) * videosPerPage
      );
    }

    const renderVideoCards = () => {
      return _videos.map((video: VideoItemProps, index: number) => {
        const videoItemIndex = isCoveoDrivenPagination
          ? index
          : videosPerPage * currentPageIndex + index;
        return (
          <VideoDetails
            key={index}
            isVideoPlayerItem={false}
            isSelectedVideo={isActiveVideoCard(video)}
            videoDetailsClasses={layoutClasses?.videoCardsList}
            videoItem={video}
            videoItemIndex={videoItemIndex}
            selectVideo={(videoId, videoItemIndex) => selectVideo(videoId, videoItemIndex, video)}
            displayStyle={displayStyle}
            sliderRef={sliderRef as SliderRefType}
          />
        );
      });
    };

    const renderCards = (): JSX.Element | JSX.Element[] => {
      switch (displayStyle) {
        case 'sidescroll':
        case 'sidescrollwithoutvideo':
        case 'sidescrollwithoutvideoandfield':
          return (
            <SliderWrapper
              sliderRef={sliderRef as SliderRefType}
              sliderSettings={sliderSettings}
              theme={'rba'}
            >
              {renderVideoCards()}
            </SliderWrapper>
          );
        case 'list':
        case 'playlist':
        default:
          return renderVideoCards();
      }
    };

    return (
      <div className={layoutClasses?.videoCardsList?.videoCardsListWrapper}>{renderCards()}</div>
    );
  };
  //#endregion

  return (
    <>
      {isMinimalSideScrollVariant && <div className="col-span-12 h-[1px] bg-gray"></div>}
      {props.fields?.headlineText?.value && (
        <div className="col-span-12">
          <Headline classes={layoutClasses.headlineText} {...props} />
        </div>
      )}
      {isVideoModalVisible &&
        ((currentScreenWidth < getBreakpoint('md') && displayStyle !== 'sidescroll') ||
          isMinimalSideScrollVariant) && (
          <VideoGalleryItem
            video={currentPlayingVideo as VideoItemProps}
            isVideoModalVisible={isVideoModalVisible}
            closeModal={closeModal}
            videoGalleryItemClasses={{ ...layoutClasses?.videoPlayer }}
          />
        )}
      {currentScreenWidth >= getBreakpoint('md') && !isMinimalSideScrollVariant && (
        <>
          <div className="col-span-12 h-[1px] bg-gray"></div>
          <VideoPlayer
            video={currentPlayingVideo as VideoItemProps}
            videoPlayerClasses={{ ...layoutClasses?.videoPlayer }}
            playerRegion={playerRegion}
          />
          {displayStyle !== 'list' && <div className="col-span-12 h-[1px] bg-gray"></div>}
        </>
      )}
      {renderVideoCardsList()}
      {
        // Don't render pagination if display style is not list
        displayStyle === 'list' &&
          (isCoveoDrivenPagination && pagerController ? (
            <div className="col-span-12">
              <Pager pagerClasses={pagerThemeData} controller={pagerController} />
            </div>
          ) : (
            fields.videos.length > 5 && (
              <Pagination
                totalPages={fields.videos.length}
                currentPageIndex={currentPageIndex}
                setCurrentPageIndex={setCurrentPageIndex}
                paginationClasses={layoutClasses?.pagination}
              />
            )
          ))
      }
      {displayStyle === 'sidescrollwithoutvideoandfield' &&
        currentScreenWidth >= getBreakpoint('md') && (
          <div className="col-span-12 h-[1px] bg-gray"></div>
        )}
    </>
  );
};

export default VideoListing;
